<script lang="ts">
    import { baseURL, socialMedia, websiteName, email } from "../../configs/config"
    import { langField } from "../../i18n"
    import { location } from "../../i18n"
    import { breadcrumbHeader, navigationIdToPathHierarchyMap, navigationIdToPathMap } from "../../store"
    import { withImageCacheBuster } from "../../utils"
    import { getParentHierarchy } from "../ecommerce/product/preview/widgets/SidebarNavigation.svelte"
    interface Props {
        createdAt?: Date | string
        updatedAt?: Date | string
        // article?: boolean // isArticle – bei Bedarf aktivieren
        // blog?: boolean // Blog – bei Bedarf aktivieren
        FAQ?: boolean
        FAQDetails?: {
            question?: string
            answer?: string
        }[]
        metaDescription?: string
        title?: string
        product?: TProduct
    }
    let {
        createdAt = new Date(),
        updatedAt = new Date(),
        // article = false,
        // blog = false,
        FAQ = false,
        FAQDetails = [],
        metaDescription = "",
        title = "",
        product,
    }: Props = $props()
    createdAt = new Date(createdAt).toISOString().split("T")[0]
    updatedAt = new Date(updatedAt).toISOString().split("T")[0]

    interface BreadcrumbCandidate {
        id: string
        title: string
        path: string
        depth: number
    }

    type BreadcrumbPathMap = Record<string, string>

    function extractBreadcrumbArray(hierarchy: NavigationIdToPathHierarchyMap | null) {
        const breadcrumbs: { id: string; value: string }[] = []

        function traverse(map: NavigationIdToPathHierarchyMap | null) {
            for (const [id, [value, children]] of Object.entries(map || {})) {
                breadcrumbs.push({ id, value })
                if (children && typeof children === "object") traverse(children)
            }
        }

        traverse(hierarchy)
        return breadcrumbs
    }

    function normalizeText(text: string): string {
        return text
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, " ")
            .trim()
    }

    function stemToken(token: string): string {
        return token.replace(/(ern|er|en|e|n|s)$/i, "")
    }

    function flattenNavigationCandidates(
        hierarchyMap: NavigationIdToPathHierarchyMap,
        pathMap: BreadcrumbPathMap,
        depth: number = 1
    ): BreadcrumbCandidate[] {
        return Object.entries(hierarchyMap || {}).flatMap(([id, [value, children]]) => {
            const path = pathMap[id] || ""
            const current: BreadcrumbCandidate[] = path
                ? [
                      {
                          id,
                          title: value,
                          path,
                          depth,
                      },
                  ]
                : []
            return [...current, ...flattenNavigationCandidates(children, pathMap, depth + 1)]
        })
    }

    function scoreCandidate(candidate: BreadcrumbCandidate, searchTokens: string[]): number {
        const pathTokens = candidate.path.split("/").filter(Boolean)
        const candidateTokens = normalizeText(`${candidate.title} ${pathTokens[pathTokens.length - 1] || ""}`)
            .split(" ")
            .map(stemToken)
            .filter((token) => token.length >= 4)

        if (candidate.path === "/de" || candidate.path === "/en") return 0

        return candidateTokens.reduce((score, token) => {
            const matched = searchTokens.some(
                (searchToken) => searchToken === token || searchToken.includes(token) || token.includes(searchToken)
            )
            return matched ? score + 10 : score
        }, candidate.depth)
    }

    function buildBreadcrumbSchema(args: {
        querylessURL: string
        pageTitle: string
        product?: TProduct
        storedBreadcrumbHeader: string
        pathMap: BreadcrumbPathMap
        hierarchyMap: NavigationIdToPathHierarchyMap
    }) {
        const { querylessURL, pageTitle, product, storedBreadcrumbHeader, pathMap, hierarchyMap } = args

        if (storedBreadcrumbHeader) {
            try {
                const parsedBreadcrumb = JSON.parse(storedBreadcrumbHeader)
                const storedItems = parsedBreadcrumb?.itemListElement || []
                if (!product || storedItems.length > 2) {
                    return parsedBreadcrumb
                }
            } catch {
                // ignore broken external breadcrumb JSON and fall back below
            }
        }

        const productName = product ? langField(product.name) : ""
        if (product && Object.keys(hierarchyMap || {}).length > 0) {
            const searchTokens = normalizeText(
                [productName, langField(product.description), querylessURL].filter(Boolean).join(" ")
            )
                .split(" ")
                .map(stemToken)
                .filter((token) => token.length >= 4)

            const bestCandidate = flattenNavigationCandidates(hierarchyMap, pathMap)
                .map((candidate) => ({
                    candidate,
                    score: scoreCandidate(candidate, searchTokens),
                }))
                .filter(({ score }) => score > 10)
                .sort((candidateA, candidateB) => candidateB.score - candidateA.score)[0]?.candidate

            const hierarchy = bestCandidate ? getParentHierarchy(hierarchyMap, bestCandidate.id, true) : null
            const breadcrumbItems = extractBreadcrumbArray(hierarchy)

            if (breadcrumbItems.length > 0) {
                return {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                        {
                            "@type": "ListItem",
                            position: 1,
                            name: "Home",
                            item: baseURL,
                        },
                        ...breadcrumbItems.map((item, index) => ({
                            "@type": "ListItem",
                            position: index + 2,
                            name: item.value,
                            item: `${baseURL}${pathMap[item.id] || ""}`,
                        })),
                        {
                            "@type": "ListItem",
                            position: breadcrumbItems.length + 2,
                            name: productName || pageTitle || "Page",
                            item: querylessURL,
                        },
                    ],
                }
            }
        }

        return {
            "@type": "BreadcrumbList",
            itemListElement: [
                {
                    "@type": "ListItem",
                    position: 1,
                    name: "Home",
                    item: baseURL,
                },
                {
                    "@type": "ListItem",
                    position: 2,
                    name: productName || pageTitle || "Page",
                    item: querylessURL,
                },
            ],
        }
    }

    let schemaOrgObject = $derived.by(() => {
        const querylessURL = $location.url?.split("?")[0] || baseURL
        const defaultProps = {
            author: {
                "@id": `${baseURL}/#company`,
            },
            publisher: {
                "@id": `${baseURL}/#company`,
            },
            inLanguage: {
                "@type": "Language",
                name: "German",
            },
            datePublished: createdAt,
            dateModified: updatedAt,
            isPartOf: {
                "@id": `${baseURL}/#website`,
            },
        }

        const schemaOrgEntity = {
            "@type": ["Store", "Organization"],
            "@id": `${baseURL}/#company`,
            name: websiteName,
            url: baseURL,
            telephone: "+4971443010",
            email,
            image: {
                "@type": "ImageObject",
                "@id": `${baseURL}/#image`,
                inLanguage: {
                    "@type": "Language",
                    name: "German",
                },
                contentUrl: `${baseURL}/api/_/assets/logo/logo.png`,
                url: `${baseURL}/api/_/assets/logo/logo.png`,
                width: 576,
                height: 158,
                caption: "Renz Logo",
            },
            logo: {
                "@type": "ImageObject",
                "@id": `${baseURL}/#logo`,
                url: baseURL,
                contentUrl: `${baseURL}/api/_/assets/logo/logo.png`,
                caption: "Renz Logo",
                inLanguage: {
                    "@type": "Language",
                    name: "German",
                },
            },
            address: {
                "@type": "PostalAddress",
                streetAddress: "Boschstraße 3",
                addressLocality: "Kirchberg",
                postalCode: "71737",
                addressCountry: "DE",
            },
            priceRange: "$$",
            sameAs: [socialMedia.instagram, socialMedia.facebook, socialMedia.linkedIn, socialMedia.xing],
        }

        const schemaOrgWebsite = {
            "@type": "WebSite",
            "@id": `${baseURL}/#website`,
            url: baseURL,
            name: "Renz",
            description: "Renz Online Store",
            ...defaultProps,
        }
        delete schemaOrgWebsite["isPartOf"]

        const schemaOrgWebPage = {
            "@type": "WebPage",
            "@id": `${querylessURL}#webpage`,
            url: querylessURL,
            name: title,
            ...defaultProps,
            description: metaDescription,
            potentialAction: [
                {
                    "@type": "ReadAction",
                    target: [querylessURL],
                },
            ],
        }
        if (!schemaOrgWebPage.description) delete schemaOrgWebPage.description

        /* isArticle – bei Bedarf aktivieren
        let schemaOrgArticle = null
        if (article || blog) {
            schemaOrgArticle = {
                "@type": "Article",
                "@id": `${querylessURL}#article`,
                headline: title,
                description: metaDescription,
                ...defaultProps,
                articleSection: ["blog"],
            }
            if (!schemaOrgArticle.description) delete schemaOrgArticle.description
        }

        let schemaOrgBlog = null
        if (blog) {
            schemaOrgBlog = {
                "@type": "BlogPosting",
                "@id": `${querylessURL}#blog`,
                headline: title,
                description: metaDescription,
                ...defaultProps,
            }
            if (!schemaOrgBlog.description) delete schemaOrgBlog.description
        }
        */

        let schemaOrgArticle = null // isArticle – bei Bedarf aktivieren (siehe Block oben)
        let schemaOrgBlog = null // Blog – bei Bedarf aktivieren (siehe Block oben)

        let schemaOrgFAQ = null
        if (FAQ) {
            schemaOrgFAQ = {
                "@type": "FAQPage",
                "@id": `${querylessURL}#faq`,
                ...defaultProps,
                mainEntity: FAQDetails.map((faq) => ({
                    "@type": "Question",
                    name: faq.question,
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: faq.answer,
                    },
                })),
            }
        }

        let schemaOrgProduct = null
        if (product) {
            const priceValidUntil = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
            schemaOrgProduct = {
                "@type": "Product",
                "@id": `${querylessURL}/#product`,
                ...defaultProps,
                image: {
                    "@type": "ImageObject",
                    url: product.images[0]?.file?.src
                        ? withImageCacheBuster(product.images[0].file.src)
                        : undefined,
                    caption: langField(product.name),
                },
                name: langField(product.name),
                description: langField(product.description),
                gtin: product.gtin,
                sku: product.code,
                brand: {
                    "@type": "Brand",
                    name: "Renz",
                },
                mainEntityOfPage: {
                    "@id": `${querylessURL}#webpage`,
                },
                releaseDate: createdAt,
                dateModified: updatedAt,
                url: querylessURL,
                offers: {
                    "@type": "Offer",
                    availability: "https://schema.org/InStock",
                    url: querylessURL,
                    price: Math.round((product.price_net_end_customer || 0) * 100) / 100,
                    priceValidUntil: priceValidUntil,
                    priceCurrency: "EUR",
                    itemCondition: "https://schema.org/NewCondition",
                    seller: {
                        "@type": "Organization",
                        "@id": `${baseURL}/#company`,
                    },
                },
            }
            if (!schemaOrgProduct.description) delete schemaOrgProduct.description
            if (product.amountOfRatings) {
                // @ts-ignore
                schemaOrgProduct.aggregateRating = {
                    "@type": "AggregateRating",
                    ratingValue: product.averageRating,
                    reviewCount: product.amountOfRatings,
                    worstRating: 1,
                    bestRating: 5,
                }
            }
            if (product.ratings && product.ratings.length > 0) {
                const reviews = (product.ratings || []).map((rating) => {
                    if (!rating) return null
                    const cleanText = rating.comment ? rating.comment.replace(/<[^>]+>/g, "") : "No comment"
                    return {
                        "@type": "Review",
                        author: {
                            "@type": "Person",
                            name: rating.author || "Anonymous",
                        },
                        datePublished: new Date(rating.review_date).toISOString().split("T")[0],
                        reviewBody: cleanText,
                        name: cleanText,
                        reviewRating: {
                            "@type": "Rating",
                            ratingValue: rating.rating || 5,
                            bestRating: 5,
                            worstRating: 1,
                        },
                    }
                })
                // @ts-ignore
                schemaOrgProduct.review = [...reviews.filter(Boolean)]
            }
        }

        const breadcrumbSchema = buildBreadcrumbSchema({
            querylessURL,
            pageTitle: title,
            product,
            storedBreadcrumbHeader: $breadcrumbHeader,
            pathMap: $navigationIdToPathMap,
            hierarchyMap: $navigationIdToPathHierarchyMap,
        })

        return {
            "@context": "https://schema.org",
            "@graph": [
                schemaOrgEntity,
                schemaOrgWebsite,
                schemaOrgWebPage,
                schemaOrgProduct,
                schemaOrgFAQ,
                schemaOrgArticle,
                schemaOrgBlog,
                breadcrumbSchema,
            ].filter(Boolean),
        }
    })

    let jsonLdScript = $derived(`
		<script type="application/ld+json">
			${JSON.stringify(schemaOrgObject)}
		${"<"}/script>
	`)
</script>

<svelte:head>
    {@html jsonLdScript}
</svelte:head>
